<template>
  <div class="about min-h-screen">
    <!-- Hero -->
    <section class="about-hero px-4 sm:px-6 lg:px-8 pt-12 pb-16 border-b border-gray-800/80">
      <div class="max-w-4xl mx-auto">
        <p class="text-teal-400 text-sm font-medium tracking-widest uppercase mb-4">About</p>
        <h1 class="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-5">
          Jerome Brown
        </h1>
        <p class="text-xl sm:text-2xl text-gray-300 font-light leading-snug max-w-2xl mb-6">
          Product leader and builder — I turn complex problems into software people actually use.
        </p>
        <p class="text-gray-400 leading-relaxed max-w-2xl mb-8">
          From wealth management and construction tech to marketplaces and AI, I've spent my career at the
          intersection of product strategy and hands-on execution — defining what to build, then
          building it.
        </p>
        <div class="flex flex-wrap items-center gap-4">
          <a
            href="mailto:jerome@jeromejbrown.com"
            class="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
          >
            <i class="fas fa-envelope text-sm"></i>
            jerome@jeromejbrown.com
          </a>
          <span class="hidden sm:inline text-gray-700">|</span>
          <router-link
            to="/contact"
            class="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            Send a message
            <i class="fas fa-arrow-right text-xs"></i>
          </router-link>
        </div>
      </div>
    </section>

    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
      <!-- Narrative -->
      <section class="grid gap-12 sm:grid-cols-3">
        <article v-for="block in sections" :key="block.title">
          <h2 class="text-white font-semibold mb-3">{{ block.title }}</h2>
          <p class="text-gray-400 text-sm leading-relaxed">{{ block.body }}</p>
        </article>
      </section>

      <!-- Experience -->
      <section>
        <div class="flex items-end justify-between mb-8">
          <div>
            <h2 class="text-2xl font-bold text-white">Experience</h2>
            <p class="text-gray-500 text-sm mt-1">Product, growth, and operations across industries</p>
          </div>
        </div>

        <div class="rounded-2xl border border-gray-700/50 bg-gray-800/30 overflow-hidden divide-y divide-gray-700/40">
          <div
            v-for="(job, index) in experiences"
            :key="`${job.company}-${index}`"
            class="experience-row group flex items-center gap-5 px-5 py-4 sm:px-6 sm:py-5 hover:bg-gray-800/50 transition-colors"
          >
            <LogoFrame :src="job.logo" :alt="`${job.company} logo`" />
            <div class="flex-1 min-w-0">
              <h3 class="font-semibold text-white group-hover:text-teal-400 transition-colors">
                <a
                  v-if="job.url"
                  :href="job.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="hover:text-teal-400 transition-colors"
                >
                  {{ job.company }}
                </a>
                <template v-else>{{ job.company }}</template>
              </h3>
              <p class="text-sm text-gray-400 mt-0.5">{{ job.role }}</p>
            </div>
            <span v-if="job.sector" class="hidden sm:block text-xs text-gray-500 shrink-0">
              {{ job.sector }}
            </span>
          </div>
        </div>
      </section>

      <!-- Skills -->
      <section class="grid gap-12 lg:grid-cols-2">
        <div>
          <h2 class="text-2xl font-bold text-white mb-2">Product</h2>
          <p class="text-gray-500 text-sm mb-6">How I drive outcomes</p>
          <ul class="space-y-3">
            <li
              v-for="skill in productSkills"
              :key="skill"
              class="flex items-start gap-3 text-gray-300 text-sm"
            >
              <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0"></span>
              {{ skill }}
            </li>
          </ul>
        </div>

        <div>
          <h2 class="text-2xl font-bold text-white mb-2">Technical</h2>
          <p class="text-gray-500 text-sm mb-6">Tools I build and ship with</p>

          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Development</p>
          <div class="grid grid-cols-4 sm:grid-cols-5 gap-3 mb-6">
            <div v-for="skill in devSkills" :key="skill.name" class="flex flex-col items-center gap-2">
              <LogoFrame :src="skill.icon" :alt="`${skill.name} logo`" size="sm" />
              <span class="text-[11px] text-gray-500 text-center leading-tight">{{ skill.name }}</span>
            </div>
          </div>

          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Product & Analytics</p>
          <div class="grid grid-cols-4 sm:grid-cols-5 gap-3 mb-6">
            <div v-for="skill in pmToolSkills" :key="skill.name" class="flex flex-col items-center gap-2">
              <LogoFrame :src="skill.icon" :alt="`${skill.name} logo`" size="sm" />
              <span class="text-[11px] text-gray-500 text-center leading-tight">{{ skill.name }}</span>
            </div>
          </div>

          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">AI</p>
          <div class="grid grid-cols-4 sm:grid-cols-5 gap-3">
            <div v-for="skill in aiSkills" :key="skill.name" class="flex flex-col items-center gap-2">
              <LogoFrame :src="skill.icon" :alt="`${skill.name} logo`" size="sm" />
              <span class="text-[11px] text-gray-500 text-center leading-tight">{{ skill.name }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="text-center py-8 border-t border-gray-800">
        <p class="text-gray-400 mb-6 max-w-md mx-auto leading-relaxed">
          Whether you're hiring, exploring a partnership, or just want to talk product —
          I'd like to hear from you.
        </p>
        <router-link
          to="/contact"
          class="inline-flex items-center gap-2 px-8 py-3 bg-teal-500 hover:bg-teal-400 text-gray-900 font-semibold rounded-full transition-colors"
        >
          Get in touch
          <i class="fas fa-arrow-right text-sm"></i>
        </router-link>
      </section>
    </div>
  </div>
</template>

<script setup>
import LogoFrame from './LogoFrame.vue';

const sections = [
  {
    title: 'Builder first',
    body: 'I started as someone who needed to see ideas working, not just documented. That instinct still drives me — I prototype early, test assumptions fast, and stay close to the code when it matters.',
  },
  {
    title: 'Systems thinker',
    body: 'Good products sit inside systems: users, incentives, data, and operations. I map those connections before committing to a direction, then reduce scope until what remains is sharp and shippable.',
  },
  {
    title: 'Beyond the desk',
    body: 'Cooking, hiking, Italian, philosophy, and behavioral economics. I mentor people entering tech and believe the best work comes from people who are curious outside their job title.',
  },
];

const productSkills = [
  'Product strategy & roadmap ownership',
  '0→1 discovery, user research & validation',
  'PRD writing, user stories & acceptance criteria',
  'Wireframing & prototyping (Figma)',
  'Cross-functional team leadership',
  'Stakeholder alignment & executive communication',
  'Data-informed prioritization (RICE, ICE, OKRs)',
  'A/B testing, experimentation & funnel analysis',
  'Agile / Scrum delivery at startup and enterprise scale',
  'Go-to-market planning & launch coordination',
  'Competitive & market analysis',
  'API, platform & integration product design',
  'SEO, discoverability & organic growth',
  'Workflow automation (Zapier) & integrations',
  'AI agents, LLM workflows & copilot tooling',
  'Vendor evaluation, RFPs & build-vs-buy decisions',
];

const devSkills = [
  { name: 'Vue.js', icon: '/assets/vue.png' },
  { name: 'Angular', icon: '/assets/angular.png' },
  { name: 'JavaScript', icon: '/assets/javascript.png' },
  { name: 'TypeScript', icon: '/assets/typescript.png' },
  { name: 'C#', icon: '/assets/csharp.png' },
  { name: '.NET', icon: '/assets/dotnetcore.png' },
  { name: 'SQL', icon: '/assets/sqlserver.png' },
  { name: 'Azure', icon: '/assets/azure.png' },
  { name: 'HTML/CSS', icon: '/assets/htmlcss.png' },
  { name: 'SEO', icon: '/assets/seo.svg' },
  { name: 'GitHub', icon: '/assets/github.png' },
  { name: 'Agile', icon: '/assets/agile.png' },
];

const pmToolSkills = [
  { name: 'Figma', icon: '/assets/figma.svg' },
  { name: 'Jira', icon: '/assets/jira.png' },
  { name: 'Zapier', icon: '/assets/zapier.png' },
  { name: 'Salesforce', icon: '/assets/salesforce.png' },
  { name: 'Dynamics 365', icon: '/assets/dynamics365.png' },
  { name: 'Power BI', icon: '/assets/powerbi.png' },
  { name: 'Tableau', icon: '/assets/tableau.png' },
  { name: 'Mixpanel', icon: '/assets/mixpanel.png' },
];

const aiSkills = [
  { name: 'OpenAI', icon: '/assets/openai.svg' },
  { name: 'MS Copilot', icon: '/assets/copilot.png' },
  { name: 'Cursor', icon: '/assets/cursor.png' },
  { name: 'Claude', icon: '/assets/claude.png' },
  { name: 'Agents', icon: '/assets/agents.svg' },
  { name: 'AI / LLMs', icon: '/assets/ai.svg' },
];

const experiences = [
  { company: 'Baird', role: 'Product Management', sector: 'Wealth management', logo: '/assets/Baird.png' },
  { company: 'Paradigm', role: 'Product Management', sector: 'Construction software', logo: '/assets/Paradigm.png', url: 'https://www.myparadigm.com' },
  { company: 'Landmark Credit Union', role: 'Product Management', sector: 'Financial services', logo: '/assets/LMCU.png' },
  { company: 'EatStreet', role: 'Product Management · BizOps', sector: 'Marketplace', logo: '/assets/EatStreet.png' },
  { company: 'UW Credit Union', role: 'Business Development', sector: 'Financial services', logo: '/assets/UWCU.png' },
  { company: 'BoostUp', role: 'Growth', sector: 'Sales enablement', logo: '/assets/BoostUp.png' },
  { company: 'ManpowerGroup', role: 'Innovation Consultant', sector: 'Workforce solutions', logo: '/assets/ManpowerGroup.png' },
  { company: 'Rocket Mortgage', role: 'Client Relations', sector: 'Mortgage & fintech', logo: '/assets/RocketMortgage.png' },
];
</script>

<style scoped>
.about-hero {
  background: linear-gradient(180deg, rgba(17, 24, 39, 0.6) 0%, transparent 100%);
}
</style>
